import { create } from 'zustand';
import type { CampaignAudienceDto, CampaignDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import type { CampaignChannel, CampaignRequest } from '@/lib/marketing';

/**
 * Campaigns — broadcasts to the customers who agreed to hear from the store
 * (ADR-036). API → Store → Render: the page reads this, and only this calls
 * the API.
 *
 * Owner-only on the API, so the page asks for nothing until it knows who is
 * looking. The list's error and the editor's are kept apart: a draft the API
 * refused stays open beside its error rather than closing on it.
 */

/** Split, because the two kinds of consent are not the same (ADR-040). */
export interface CampaignReach {
  count: number;
  accounts: number;
  subscribers: number;
}

interface CampaignsState {
  items: CampaignDto[];
  nextCursor: string | null;
  loading: boolean;
  loadingMore: boolean;
  /** A row's send, stop, or delete in flight. */
  acting: boolean;
  /** The list's own trouble. */
  error: string | null;

  /** The draft open in the editor, as the API has it now. */
  current: CampaignDto | null;
  currentLoading: boolean;
  saving: boolean;
  testing: boolean;
  /** Why the editor's last save, test, or opening was refused. */
  saveError: string | null;

  load: () => Promise<void>;
  loadMore: () => Promise<void>;
  /** Re-reads the campaigns being sent, so their counts move while the page is open. */
  refreshSending: () => Promise<void>;
  open: (id: string) => Promise<void>;
  clearSaveError: () => void;
  create: (input: CampaignRequest) => Promise<boolean>;
  update: (id: string, input: Omit<CampaignRequest, 'channel'>) => Promise<boolean>;
  /** Whether the test went out; null when the API refused to try. */
  sendTest: (id: string, to: string) => Promise<boolean | null>;
  /** How many consenting customers an audience reaches now; null when it could not be counted. */
  estimate: (channel: CampaignChannel, audience: CampaignAudienceDto) => Promise<CampaignReach | null>;
  /** Now, or at `at` — a scheduled campaign waits for its time (ADR-099). */
  send: (id: string, at?: string) => Promise<boolean>;
  unschedule: (id: string) => Promise<boolean>;
  /** While an A/B test waits (ADR-113): this subject goes to the rest now. */
  pickWinner: (id: string, variant: 'A' | 'B') => Promise<boolean>;
  cancel: (id: string) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
}

const PAGE_SIZE = 20;


function replace(items: CampaignDto[], updated: CampaignDto): CampaignDto[] {
  return items.map((item) => (item.id === updated.id ? updated : item));
}

export const useCampaignsStore = create<CampaignsState>((set, get) => ({
  items: [],
  nextCursor: null,
  loading: false,
  loadingMore: false,
  acting: false,
  error: null,
  current: null,
  currentLoading: false,
  saving: false,
  testing: false,
  saveError: null,

  async load() {
    set({ loading: true, error: null });

    try {
      const page = await withSession(() => adminApi.campaigns.list({ limit: PAGE_SIZE }));
      set({ items: page.items, nextCursor: page.meta.nextCursor, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load campaigns.') });
    }
  },

  async loadMore() {
    const { nextCursor, loadingMore, items } = get();
    if (!nextCursor || loadingMore) return;

    set({ loadingMore: true });

    try {
      const page = await withSession(() => adminApi.campaigns.list({ limit: PAGE_SIZE, cursor: nextCursor }));
      set({ items: [...items, ...page.items], nextCursor: page.meta.nextCursor, loadingMore: false });
    } catch (error) {
      set({ loadingMore: false, error: errorMessage(error, 'Could not load more campaigns.') });
    }
  },

  async refreshSending() {
    // A test's mails going out count as sending; its wait does not need a five-second clock (ADR-113).
    const sending = get().items.filter((item) => item.status === 'SENDING' || (item.status === 'TESTING' && !item.abTest?.testEndsAt));
    if (sending.length === 0) return;

    try {
      const fresh = await Promise.all(sending.map((item) => withSession(() => adminApi.campaigns.get(item.id))));
      set((state) => ({ items: fresh.reduce(replace, state.items) }));
    } catch {
      // The next tick asks again; the list keeps the counts it had.
    }
  },

  async open(id) {
    set({ current: null, currentLoading: true, saveError: null });

    try {
      const campaign = await withSession(() => adminApi.campaigns.get(id));
      set((state) => ({ current: campaign, currentLoading: false, items: replace(state.items, campaign) }));
    } catch (error) {
      set({ currentLoading: false, saveError: errorMessage(error, 'Could not open this campaign.') });
    }
  },

  clearSaveError() {
    set({ saveError: null });
  },

  async create(input) {
    set({ saving: true, saveError: null });

    try {
      const created = await withSession(() => adminApi.campaigns.create(input));
      set((state) => ({ saving: false, items: [created, ...state.items] }));
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not save this draft.') });
      return false;
    }
  },

  async update(id, input) {
    set({ saving: true, saveError: null });

    try {
      const updated = await withSession(() => adminApi.campaigns.update(id, input));
      set((state) => ({ saving: false, current: updated, items: replace(state.items, updated) }));
      return true;
    } catch (error) {
      // "Only a draft can be changed" — someone sent it from another tab.
      set({ saving: false, saveError: errorMessage(error, 'Could not save this draft.') });
      return false;
    }
  },

  async sendTest(id, to) {
    set({ testing: true, saveError: null });

    try {
      const result = await withSession(() => adminApi.campaigns.test(id, to));
      set({ testing: false });
      return result.sent;
    } catch (error) {
      set({ testing: false, saveError: errorMessage(error, 'Could not send the test.') });
      return null;
    }
  },

  async estimate(channel, audience) {
    try {
      return await withSession(() => adminApi.campaigns.audience({ channel, audience }));
    } catch {
      // A count that cannot be had is shown as unknown, never as zero.
      return null;
    }
  },

  async send(id, at) {
    set({ acting: true, error: null });

    try {
      const updated = await withSession(() => adminApi.campaigns.send(id, at));
      set((state) => ({ acting: false, items: replace(state.items, updated) }));
      return true;
    } catch (error) {
      // "Nobody matches" and "set the storefront address first" are the API's to say.
      set({ acting: false, error: errorMessage(error, 'Could not send this campaign.') });
      return false;
    }
  },

  async unschedule(id) {
    set({ acting: true, error: null });
    try {
      const updated = await withSession(() => adminApi.campaigns.unschedule(id));
      set((state) => ({ acting: false, items: replace(state.items, updated) }));
      return true;
    } catch (error) {
      set({ acting: false, error: errorMessage(error, 'Could not take this campaign off its schedule.') });
      return false;
    }
  },

  async pickWinner(id, variant) {
    set({ acting: true, error: null });
    try {
      const updated = await withSession(() => adminApi.campaigns.pickWinner(id, variant));
      set((state) => ({ acting: false, items: replace(state.items, updated) }));
      return true;
    } catch (error) {
      set({ acting: false, error: errorMessage(error, 'Could not send the winner.') });
      return false;
    }
  },

  async cancel(id) {
    set({ acting: true, error: null });

    try {
      const updated = await withSession(() => adminApi.campaigns.cancel(id));
      set((state) => ({ acting: false, items: replace(state.items, updated) }));
      return true;
    } catch (error) {
      set({ acting: false, error: errorMessage(error, 'Could not stop this campaign.') });
      return false;
    }
  },

  async remove(id) {
    set({ acting: true, error: null });

    try {
      await withSession(() => adminApi.campaigns.remove(id));
      set((state) => ({ acting: false, items: state.items.filter((item) => item.id !== id) }));
      return true;
    } catch (error) {
      set({ acting: false, error: errorMessage(error, 'Could not delete this campaign.') });
      return false;
    }
  },
}));
